import { useMemo, useState } from "react";
import type { UseQueryResult, UseMutationResult } from "@tanstack/react-query";
import type { DialpadSyncWindow, DialpadLinkedAssessmentRun } from "../../lib/api";
import { fmtDateTime, fmtDurationMs, fmtRange, fmtSeconds } from "../../lib/format";
import { CollapsibleSection } from "./shared";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface BatchGroup {
  key: string;
  batchId: string | null;
  windows: DialpadSyncWindow[];
}

interface AggRow {
  rangeStart: string;
  rangeEnd: string;
  status: DialpadSyncWindow["status"];
  phase: string | null;
  phases: string | null;
  callsFound: number;
  callsIngested: number;
  transcriptsIngested: number;
  skipShort: number;
  skipNoDur: number;
  minDurationMs: number | null;
  errors: number;
  recapErrors: number;
  assessmentRun: DialpadLinkedAssessmentRun | null;
  ugStatus: string | null;
  usersUpserted: number | null;
  groupsUpserted: number | null;
  ugStartedAt: string | null;
  ugFinishedAt: string | null;
  ugError: string | null;
  durationMs: number | null;
  triggeredBy: string | null;
  updatedAt: string;
  lastError: string | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function aggregateGroup(windows: DialpadSyncWindow[]): AggRow {
  // Callers guarantee windows is non-empty (batch groups always have ≥1).
  const first = windows[0]!;
  const rangeStart = windows.reduce(
    (a, w) => (w.window_start < a ? w.window_start : a),
    first.window_start,
  );
  const rangeEnd = windows.reduce(
    (a, w) => (w.window_end > a ? w.window_end : a),
    first.window_end,
  );

  // Status precedence: running > pending > failed > complete_with_errors > complete.
  const statusPri: Record<string, number> = {
    running: 5,
    pending: 4,
    failed: 3,
    complete_with_errors: 2,
    complete: 1,
  };
  const status = windows.reduce<DialpadSyncWindow["status"]>(
    (acc, w) => ((statusPri[w.status] ?? 0) > (statusPri[acc] ?? 0) ? w.status : acc),
    first.status,
  );

  // Phase: earliest non-complete sibling phase.
  const phaseOrd: Record<string, number> = {
    queued: 1,
    users_groups: 2,
    calls: 3,
    transcripts: 4,
    assessments: 5,
    complete: 6,
  };
  const activePhases = windows
    .filter((w) => w.status === "pending" || w.status === "running")
    .map((w) => w.phase ?? "queued");
  const phase =
    activePhases.length > 0
      ? activePhases.reduce((a, b) =>
          (phaseOrd[a] ?? 0) <= (phaseOrd[b] ?? 0) ? a : b,
        )
      : windows.reduce(
          (a, w) =>
            (phaseOrd[w.phase ?? "complete"] ?? 0) > (phaseOrd[a] ?? 0)
              ? (w.phase ?? "complete")
              : a,
          "complete",
        );

  const callsFound = windows.reduce((s, w) => s + w.calls_found, 0);
  const callsIngested = windows.reduce((s, w) => s + w.calls_ingested, 0);
  const transcriptsIngested = windows.reduce((s, w) => s + w.transcripts_ingested, 0);
  const skipShort = windows.reduce((s, w) => s + (w.transcripts_skipped_short ?? 0), 0);
  const skipNoDur = windows.reduce((s, w) => s + (w.transcripts_skipped_no_duration ?? 0), 0);
  const errors = windows.reduce((s, w) => s + w.errors, 0);
  const recapErrors = windows.reduce((s, w) => s + (w.recap_errors ?? 0), 0);

  // Assessment run: aggregate across all linked runs.
  const ars = windows
    .map((w) => w.assessment_run)
    .filter((a): a is DialpadLinkedAssessmentRun => a != null);
  let assessmentRun: DialpadLinkedAssessmentRun | null = null;
  if (ars.length > 0) {
    const total = ars.reduce((s, a) => s + a.progress.total, 0);
    const completed = ars.reduce((s, a) => s + a.progress.completed, 0);
    const failed = ars.reduce((s, a) => s + a.progress.failed, 0);
    const skipped = ars.reduce((s, a) => s + a.progress.skipped, 0);
    const arStatuses = ars.map((a) => a.status);
    let arStatus: DialpadLinkedAssessmentRun["status"] = "completed";
    if (arStatuses.includes("running")) arStatus = "running";
    else if (arStatuses.includes("queued")) arStatus = "queued";
    else if (arStatuses.includes("failed")) arStatus = "failed";
    else if (arStatuses.includes("partial")) arStatus = "partial";
    assessmentRun = {
      run_id: ars[0]!.run_id,
      status: arStatus,
      progress: { total, completed, failed, skipped },
    };
  }

  // Users/groups: first completed sibling (idempotent — avoid N× count).
  const ugDone = windows.find(
    (w) => w.users_groups_status === "succeeded" || w.users_groups_status === "failed",
  );
  const ugStatuses = windows
    .map((w) => w.users_groups_status)
    .filter((s): s is string => s != null);
  let ugStatus: string | null = null;
  if (ugStatuses.some((s) => s === "running" || s === "pending")) ugStatus = "running";
  else if (ugStatuses.some((s) => s === "failed")) ugStatus = "failed";
  else if (ugStatuses.length > 0) ugStatus = "succeeded";

  const ugStarts = windows
    .map((w) => w.users_groups_started_at)
    .filter((s): s is string => s != null);
  const ugFinishes = windows
    .map((w) => w.users_groups_finished_at)
    .filter((s): s is string => s != null);

  // Duration: span from earliest created_at to latest updated_at.
  const durationMsVals = windows
    .map((w) => w.duration_ms)
    .filter((v): v is number => v != null);
  const durationMs = durationMsVals.length > 0 ? Math.max(...durationMsVals) : null;

  const updatedAt = windows.reduce(
    (a, w) => (w.updated_at > a ? w.updated_at : a),
    first.updated_at,
  );

  return {
    rangeStart,
    rangeEnd,
    status,
    phase: status === "running" || status === "pending" ? phase : null,
    phases: first.phases ?? null,
    callsFound,
    callsIngested,
    transcriptsIngested,
    skipShort,
    skipNoDur,
    minDurationMs: first.min_duration_ms ?? null,
    errors,
    recapErrors,
    assessmentRun,
    ugStatus,
    usersUpserted: ugDone?.users_upserted ?? null,
    groupsUpserted: ugDone?.groups_upserted ?? null,
    ugStartedAt: ugStarts.length > 0 ? ugStarts.reduce((a, b) => (a < b ? a : b)) : null,
    ugFinishedAt:
      ugStatus === "running"
        ? null
        : ugFinishes.length > 0
          ? ugFinishes.reduce((a, b) => (a > b ? a : b))
          : null,
    ugError: windows.find((w) => w.users_groups_error)?.users_groups_error ?? null,
    durationMs,
    triggeredBy: first.triggered_by,
    updatedAt,
    lastError: windows.find((w) => w.last_error)?.last_error ?? null,
  };
}

// ---------------------------------------------------------------------------
// Row renderers
// ---------------------------------------------------------------------------

/** Render the cells inside a <tr> for a single window or aggregate. */
function SyncRowCells({
  rangeStart,
  rangeEnd,
  status,
  phase,
  phases,
  callsFound,
  callsIngested,
  transcriptsIngested,
  skipShort,
  skipNoDur,
  minDurationMs,
  errors,
  recapErrors,
  assessmentRun,
  ugStatus,
  usersUpserted,
  groupsUpserted,
  ugStartedAt,
  ugFinishedAt,
  ugError,
  durationMs,
  triggeredBy,
  updatedAt,
  lastError,
  nowTick,
  prefix,
}: AggRow & { nowTick: number; prefix?: React.ReactNode }) {
  const p2Errors = errors + recapErrors;
  const p3Failed = assessmentRun?.progress.failed ?? 0;
  const totalErrors = p2Errors + p3Failed;
  const hasErrors = totalErrors > 0;
  const effectiveStatus =
    status === "complete" && hasErrors ? "complete_with_errors" : status;

  return (
    <>
      <td className="px-3 py-2 text-xs">
        {prefix}
        {fmtRange(rangeStart, rangeEnd)}
      </td>
      <td className="px-3 py-2">
        <span
          className={`rounded px-1.5 py-0.5 text-xs ${
            effectiveStatus === "complete"
              ? "bg-primary/15 text-primary"
              : effectiveStatus === "complete_with_errors"
                ? "bg-amber-500/15 text-amber-700 dark:text-amber-400"
                : effectiveStatus === "failed"
                  ? "bg-destructive/15 text-destructive"
                  : effectiveStatus === "running"
                    ? "bg-secondary/15 text-secondary"
                    : "bg-muted text-foreground"
          }`}
          title={
            hasErrors && effectiveStatus !== "failed"
              ? `${errors} transcript + ${recapErrors} recap fetch + ${p3Failed} assessment errors`
              : undefined
          }
        >
          {effectiveStatus === "complete_with_errors"
            ? `complete (${totalErrors} errors)`
            : effectiveStatus}
        </span>
        {phase &&
          status !== "complete" &&
          status !== "complete_with_errors" && (
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground mt-0.5">
              {phase}
            </div>
          )}
        {phases && phases !== "all" && (
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mt-0.5">
            {phases === "phase1"
              ? "Phase 1 only"
              : phases === "phase2"
                ? "Phase 2 only"
                : "Phase 3 only"}
          </div>
        )}
        {lastError && (
          <div className="text-xs text-destructive mt-1 max-w-xs truncate">
            {lastError}
          </div>
        )}
      </td>
      <td className="px-3 py-2 text-right">
        {callsIngested}
        <span className="text-muted-foreground">/{callsFound}</span>
      </td>
      <td className="px-3 py-2 text-right">{transcriptsIngested}</td>
      <td className="px-3 py-2 text-right">
        {(() => {
          const ar = assessmentRun;
          if (!ar) return <span className="text-muted-foreground">—</span>;
          const s = ar.status;
          const inFlight = s === "queued" || s === "running";
          const cls =
            s === "failed"
              ? "text-destructive font-medium"
              : s === "partial"
                ? "text-amber-700 dark:text-amber-400 font-medium"
                : inFlight
                  ? "text-secondary"
                  : "text-foreground";
          const title =
            s === "failed"
              ? "Phase 3 failed"
              : s === "partial"
                ? `Phase 3 finished with ${ar.progress.failed} failed pair${ar.progress.failed === 1 ? "" : "s"}`
                : s === "completed"
                  ? "Phase 3 completed"
                  : `Phase 3 ${s}`;
          return (
            <div title={title} className={cls}>
              {ar.progress.completed}
              <span className="text-muted-foreground">/{ar.progress.total}</span>
              <div className="text-[10px] text-muted-foreground">
                pairs
                {ar.progress.failed > 0 && (
                  <span
                    className={
                      s === "failed"
                        ? "ml-1 text-destructive"
                        : "ml-1 text-amber-700 dark:text-amber-400"
                    }
                  >
                    · {ar.progress.failed} failed
                  </span>
                )}
                {inFlight && (
                  <span className="ml-1 uppercase tracking-wide">· {s}</span>
                )}
              </div>
            </div>
          );
        })()}
      </td>
      <td className="px-3 py-2 text-right text-xs text-muted-foreground">
        {(() => {
          const ugRunning = ugStatus === "running" || ugStatus === "pending";
          const startMs = ugStartedAt ? new Date(ugStartedAt).getTime() : NaN;
          const finishMs = ugFinishedAt ? new Date(ugFinishedAt).getTime() : NaN;
          let elapsed: string | null = null;
          if (Number.isFinite(startMs)) {
            const endMs = Number.isFinite(finishMs)
              ? finishMs
              : ugRunning
                ? nowTick
                : NaN;
            if (Number.isFinite(endMs)) {
              elapsed = fmtDurationMs(Math.max(0, endMs - startMs));
            }
          }
          if (ugStatus === "failed") {
            return (
              <span className="text-destructive" title={ugError ?? undefined}>
                failed
                {elapsed && (
                  <span className="ml-1 text-muted-foreground">· {elapsed}</span>
                )}
              </span>
            );
          }
          if (!ugStatus) return "—";
          return (
            <>
              <span className="text-foreground">{usersUpserted ?? 0}</span>
              {" / "}
              <span className="text-foreground">{groupsUpserted ?? 0}</span>
              {elapsed && (
                <div className="text-[10px] text-muted-foreground">
                  {elapsed}
                  {ugRunning && " elapsed"}
                </div>
              )}
            </>
          );
        })()}
      </td>
      <td className="px-3 py-2 text-right text-xs text-muted-foreground">
        {skipShort + skipNoDur}
        {skipShort + skipNoDur > 0 && (
          <div className="text-[10px] text-muted-foreground">
            short {skipShort} · n/a {skipNoDur}
          </div>
        )}
      </td>
      <td className="px-3 py-2 text-right text-xs text-muted-foreground">
        {minDurationMs != null ? fmtSeconds(minDurationMs / 1000) : "—"}
      </td>
      {(() => {
        const total = errors + recapErrors + (assessmentRun?.progress.failed ?? 0);
        return (
          <td
            className={`px-3 py-2 text-right ${total > 0 ? "text-destructive font-medium" : ""}`}
          >
            {total}
          </td>
        );
      })()}
      <td className="px-3 py-2 text-right text-xs text-muted-foreground tabular-nums">
        {durationMs != null ? fmtDurationMs(durationMs) : "—"}
      </td>
      <td className="px-3 py-2 text-xs text-muted-foreground">{triggeredBy || "—"}</td>
      <td className="px-3 py-2 text-xs text-muted-foreground">{fmtDateTime(updatedAt)}</td>
    </>
  );
}

function windowToAggRow(w: DialpadSyncWindow): AggRow {
  return {
    rangeStart: w.window_start,
    rangeEnd: w.window_end,
    status: w.status,
    phase: w.phase ?? null,
    phases: w.phases ?? null,
    callsFound: w.calls_found,
    callsIngested: w.calls_ingested,
    transcriptsIngested: w.transcripts_ingested,
    skipShort: w.transcripts_skipped_short ?? 0,
    skipNoDur: w.transcripts_skipped_no_duration ?? 0,
    minDurationMs: w.min_duration_ms ?? null,
    errors: w.errors,
    recapErrors: w.recap_errors ?? 0,
    assessmentRun: w.assessment_run ?? null,
    ugStatus: w.users_groups_status ?? null,
    usersUpserted: w.users_upserted ?? null,
    groupsUpserted: w.groups_upserted ?? null,
    ugStartedAt: w.users_groups_started_at ?? null,
    ugFinishedAt: w.users_groups_finished_at ?? null,
    ugError: w.users_groups_error ?? null,
    durationMs: w.duration_ms ?? null,
    triggeredBy: w.triggered_by,
    updatedAt: w.updated_at,
    lastError: w.last_error,
  };
}

// ---------------------------------------------------------------------------
// Batch group row with expand/collapse
// ---------------------------------------------------------------------------

function BatchGroupRows({
  group,
  nowTick,
}: {
  group: BatchGroup;
  nowTick: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const isSingle = group.windows.length === 1;

  if (isSingle) {
    const w = group.windows[0]!;
    return (
      <tr
        className="border-t border-border hover:bg-primary/5 transition-colors duration-[var(--duration-base)] ease-[var(--easing-standard)]"
      >
        <SyncRowCells {...windowToAggRow(w)} nowTick={nowTick} />
      </tr>
    );
  }

  const agg = aggregateGroup(group.windows);
  return (
    <>
      <tr
        className="border-t border-border hover:bg-primary/5 cursor-pointer transition-colors duration-[var(--duration-base)] ease-[var(--easing-standard)]"
        onClick={() => setExpanded((e) => !e)}
      >
        <SyncRowCells
          {...agg}
          nowTick={nowTick}
          prefix={
            <span
              className="inline-block mr-1.5 text-muted-foreground select-none"
              title={`${group.windows.length} sub-windows`}
            >
              {expanded ? "▾" : "▸"}
              <span className="text-[10px] ml-0.5">{group.windows.length}</span>
            </span>
          }
        />
      </tr>
      {expanded &&
        group.windows.map((w) => (
          <tr
            key={w.id}
            className="border-t border-border/50 bg-muted/20 hover:bg-primary/5 transition-colors duration-[var(--duration-base)] ease-[var(--easing-standard)]"
          >
            <SyncRowCells
              {...windowToAggRow(w)}
              nowTick={nowTick}
              prefix={<span className="inline-block w-4" />}
            />
          </tr>
        ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Exported component
// ---------------------------------------------------------------------------

export function SyncHistorySection({
  windowsQ,
  hasActive,
  clearErr,
  confirmingClear,
  setConfirmingClear,
  clearM,
  role,
  nowTick,
}: {
  windowsQ: UseQueryResult<DialpadSyncWindow[]>;
  hasActive: boolean | undefined;
  clearErr: { message?: string } | null;
  confirmingClear: boolean;
  setConfirmingClear: (v: boolean) => void;
  clearM: UseMutationResult<unknown, unknown, void>;
  role: string;
  nowTick: number;
}) {
  const batchGroups = useMemo(() => {
    const groups = new Map<string, DialpadSyncWindow[]>();
    for (const w of windowsQ.data ?? []) {
      const key = w.batch_id ?? w.id;
      const list = groups.get(key) ?? [];
      list.push(w);
      groups.set(key, list);
    }
    return [...groups.values()]
      .filter((ws) => ws.length > 0)
      .map((windows): BatchGroup => {
        const f = windows[0]!;
        return {
          key: f.batch_id ?? f.id,
          batchId: f.batch_id ?? null,
          windows,
        };
      });
  }, [windowsQ.data]);

  return (
    <CollapsibleSection
      title="Sync History"
      ariaLabel="Sync history"
      subtitle={`${batchGroups.length} sync${batchGroups.length === 1 ? "" : "s"}`}
      autoExpand={
        !!clearErr ||
        !!hasActive ||
        (windowsQ.data ?? []).some(
          (w) =>
            w.status === "failed" ||
            w.status === "complete_with_errors" ||
            w.status === "running" ||
            !!w.last_error,
        )
      }
      headerActions={
        role === "supadmin" ? (
          <div className="flex items-center gap-2">
            {clearErr && (
              <span className="text-xs text-destructive">{clearErr.message}</span>
            )}
            {confirmingClear ? (
              <>
                <span className="text-xs text-destructive">
                  Wipe all Dialpad calls, transcripts, and sync history?
                </span>
                <button
                  type="button"
                  onClick={() => clearM.mutate()}
                  disabled={clearM.isPending || !!hasActive}
                  className="rounded bg-destructive text-white text-xs px-2.5 py-1 hover:bg-destructive/90 disabled:opacity-50"
                >
                  {clearM.isPending ? "Clearing…" : "Yes, clear"}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmingClear(false)}
                  disabled={clearM.isPending}
                  className="rounded border border-input bg-card text-foreground text-xs px-2.5 py-1 hover:bg-muted/40"
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmingClear(true)}
                disabled={!!hasActive}
                title={
                  hasActive ? "Wait for the in-progress sync to finish" : undefined
                }
                className="rounded border border-destructive/30 bg-card text-destructive text-xs px-2.5 py-1 hover:bg-destructive/10 disabled:opacity-50"
              >
                Clear sync history
              </button>
            )}
          </div>
        ) : undefined
      }
    >
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-secondary/10 text-muted-foreground text-xs uppercase">
            <tr>
              <th className="text-left px-3 py-2">Range</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-right px-3 py-2">Calls</th>
              <th className="text-right px-3 py-2">Transcripts</th>
              <th className="text-right px-3 py-2">Assessments</th>
              <th className="text-right px-3 py-2">Users / Groups</th>
              <th className="text-right px-3 py-2">Skipped</th>
              <th className="text-right px-3 py-2">Min</th>
              <th className="text-right px-3 py-2">Errors</th>
              <th className="text-right px-3 py-2">Duration</th>
              <th className="text-left px-3 py-2">Triggered by</th>
              <th className="text-left px-3 py-2">Updated</th>
            </tr>
          </thead>
          <tbody>
            {windowsQ.isLoading ? (
              <tr>
                <td
                  colSpan={12}
                  className="px-3 py-6 text-center text-sm text-muted-foreground"
                >
                  Loading…
                </td>
              </tr>
            ) : batchGroups.length === 0 ? (
              <tr>
                <td
                  colSpan={12}
                  className="px-3 py-6 text-center text-sm text-muted-foreground"
                >
                  No runs yet.
                </td>
              </tr>
            ) : null}
            {batchGroups.map((group) => (
              <BatchGroupRows key={group.key} group={group} nowTick={nowTick} />
            ))}
          </tbody>
        </table>
      </div>
    </CollapsibleSection>
  );
}
